import React, { useEffect, useState } from 'react';
import ErrorIcon from '@mui/icons-material/Error';
import { type FormikProp, type FormikStatus } from './types';
import { IDENTITY_DOCUMENT_TYPE, IDENTITY_TYPE } from './constants';
import { dependantVisaOptions } from './options';
import { PAGE_TEXT } from './lang';
import { COMMON_PAGE_TEXT, Radio, Select } from '../../../components';
import HKID_IMG from '../assets/hkid.png';

const otherIdentityStepMap = [
  IDENTITY_TYPE.IANG,
  IDENTITY_TYPE.DEPENDANT_VISA_UNDER_18,
  IDENTITY_TYPE.DEPENDANT_VISA_ABOVE_18,
  IDENTITY_TYPE.WORK_VISA,
  IDENTITY_TYPE.HKID_RIGHT_TO_LAND,
];

export const IdentityTypeFormContent: React.FunctionComponent<FormikProp> = (
  formik: FormikProp,
) => {
  const formikStatus = formik.status as FormikStatus;
  const formikSetFieldValue = formik.setFieldValue;

  const hasValidIdentity =
    formik.values.identityType !== '' &&
    [
      IDENTITY_TYPE.HKID_PERM,
      IDENTITY_TYPE.STUDENT_VISA,
      IDENTITY_TYPE.DEPENDANT_VISA_ABOVE_18,
      IDENTITY_TYPE.DEPENDANT_VISA_UNDER_18,
    ].includes(formik.values.identityType);

  useEffect(() => {
    // declare the update form function
    const updateFormFields = async (): Promise<void> => {
      if (!hasValidIdentity) {
        await formikSetFieldValue('isLocal', false);
        await formikSetFieldValue('needStudentVisa', true);
      }
      if (
        formik.values.identityType === IDENTITY_TYPE.IANG ||
        formik.values.identityType === IDENTITY_TYPE.DEPENDANT_VISA_ABOVE_18
      ) {
        await formikSetFieldValue('isLocal', false); // other options lead to local applicant
      }
      if (formik.values.identityType === IDENTITY_TYPE.HKID_PERM) {
        await formikSetFieldValue('isLocal', true);
        await formikSetFieldValue(
          'identityDocumentType',
          IDENTITY_DOCUMENT_TYPE.HK_ID,
        );
      }
    };

    // call the function
    updateFormFields()
      // make sure to catch any error
      .catch(console.error);
  }, [formik.values.identityType]);

  // IDENTITY TYPE FLOW
  const [selectedIdentity, setSelectedIdentity] = useState<IDENTITY_TYPE>(
    (formik.values.identityType as IDENTITY_TYPE) ?? undefined,
  );
  const stepIndex = otherIdentityStepMap.indexOf(selectedIdentity);
  const [step, setStep] = useState<number>(
    stepIndex >= 2 ? stepIndex - 1 : stepIndex, // because of the two the selections
  );

  const [currentStepChecked, setCurrentStepChecked] = useState<
    boolean | undefined
  >(otherIdentityStepMap.includes(selectedIdentity) ? true : undefined);

  const [entryDescription, setEntryDescription] = useState<string>('');

  const handleOtherPermitChange = async (
    checked: boolean,
    index: number,
  ): Promise<void> => {
    setCurrentStepChecked(checked);
    setEntryDescription('');

    // click `NO` all the time
    if (step === 5 && !checked) {
      setEntryDescription(PAGE_TEXT.IDENTITY_FLOW.NONE);
      await formikSetFieldValue('identityType', '');
      return;
    }

    if (checked) {
      if (step > index) {
        setStep(index);
      }
      switch (index) {
        case 0:
          setEntryDescription(PAGE_TEXT.IDENTITY_FLOW.IANG.DESC);
          await formikSetFieldValue('identityType', IDENTITY_TYPE.IANG);
          break;
        case 2:
          // Since no API changes are expected, questions grouped together will have "One way permit"
          await formikSetFieldValue('identityType', IDENTITY_TYPE.OTHER_VISA);
          setEntryDescription(PAGE_TEXT.IDENTITY_FLOW.OTHER_PERMITS.DESC);
          break;
        // TTPS
        case 3:
          await formikSetFieldValue('identityType', IDENTITY_TYPE.TTPS);
          setEntryDescription(PAGE_TEXT.IDENTITY_FLOW.IANG.DESC);
          break;
        case 4:
          await formikSetFieldValue('identityType', IDENTITY_TYPE.WORK_VISA);
          setEntryDescription(PAGE_TEXT.IDENTITY_FLOW.EMPLOYMENT_PERMIT.DESC);
          break;
        case 5:
          await formikSetFieldValue(
            'identityType',
            IDENTITY_TYPE.HKID_RIGHT_TO_LAND,
          );
          setEntryDescription(PAGE_TEXT.IDENTITY_FLOW.RIGHT_TO_LAND.DESC);
          break;
        default:
          break;
      }
    } else {
      if (step > index) {
        setStep(index);
      } else {
        setStep(step + 1);
      }
      setCurrentStepChecked(undefined);
      await formikSetFieldValue('identityType', '');
    }
  };

  useEffect(() => {
    const stepIndex = otherIdentityStepMap.indexOf(selectedIdentity);
    if (selectedIdentity === IDENTITY_TYPE.OTHERS) {
      // only select others
      setStep(0);
    } else if (stepIndex > -1) {
      // select other and have more steps
      setStep(stepIndex >= 2 ? stepIndex - 1 : stepIndex);
    } else {
      setStep(-1);
      setCurrentStepChecked(undefined);
    }
  }, [selectedIdentity]);

  const showHelperText =
    selectedIdentity === IDENTITY_TYPE.HKID_PERM ||
    selectedIdentity === IDENTITY_TYPE.STUDENT_VISA;
  return (
    <div className="w-3/4 justify-center">
      <h2 className="flex justify-center mb-6">
        {PAGE_TEXT.OPTIONS_LABEL.IDENTITY_TYPE.HINT}
      </h2>
      <div className="w-full px-8">
        <div className="grid grid-cols-3 gap-x-12 place-items-center">
          {[
            IDENTITY_TYPE.HKID_PERM,
            IDENTITY_TYPE.STUDENT_VISA,
            IDENTITY_TYPE.OTHERS,
          ].map((type, index) => {
            let isActive = false;
            if (index > 1) {
              isActive = step >= 0;
            } else {
              isActive = selectedIdentity === type;
            }
            return (
              <div
                key={type}
                className={`w-52 h-28 cursor-pointer border border-[#951D1D] rounded grid place-items-center ${
                  isActive ? 'bg-[#951D1D] text-white' : ''
                }`}
                onClick={async () => {
                  await formikSetFieldValue('identityDocumentType', '');
                  await formikSetFieldValue('identityDocumentNumber', '');
                  await formikSetFieldValue('identityType', type);
                  setSelectedIdentity(type);
                }}
              >
                {PAGE_TEXT.OPTIONS_LABEL.IDENTITY_TYPE[type]}
              </div>
            );
          })}
        </div>
      </div>
      {PAGE_TEXT.IDENTITY_FLOW.QUESTIONS.map((question, index) =>
        index <= step ? (
          <div key={index} className="my-12 w-full text-center">
            <h3>{question}</h3>

            {step === 2 && index === 2 ? (
              <ul className="my-4">
                {PAGE_TEXT.IDENTITY_FLOW.OTHER_PERMITS.LABELS.map(label => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            ) : null}
            <div className="space-x-12">
              <Radio
                checked={index < step ? false : currentStepChecked === true}
                onClick={async () => {
                  await handleOtherPermitChange(true, index);
                }}
                label={COMMON_PAGE_TEXT.YES}
              />
              <Radio
                checked={index < step ? true : currentStepChecked === false}
                onClick={async () => {
                  await handleOtherPermitChange(false, index);
                }}
                label={COMMON_PAGE_TEXT.NO}
              />
            </div>
          </div>
        ) : (
          ''
        ),
      )}

      {selectedIdentity === IDENTITY_TYPE.HKID_PERM ? (
        <div className="w-full flex items-center justify-center mt-8">
          <img src={HKID_IMG} alt="HKID image" className="w-[15rem]" />
        </div>
      ) : (
        ''
      )}
      {showHelperText && (
        <div className="flex flex-col	justify-center px-14 mt-8 w-full">
          <span className="flex items-center text-[#3E75AC] my-2">
            <ErrorIcon
              sx={{ color: '#3E75AC', width: '20px', height: '20px' }}
            />
            {PAGE_TEXT.HELPER_TEXT.EXPLANATION}
          </span>
          {selectedIdentity === IDENTITY_TYPE.HKID_PERM ? (
            <h2>
              {PAGE_TEXT.HELPER_TEXT.HK_PERMANENT_ID_CARD.CONTENT_REQUIRED}
            </h2>
          ) : (
            <h2>{PAGE_TEXT.HELPER_TEXT.STUDENT_VISA.CONTENT_REQUIRED}</h2>
          )}

          <span className="flex items-center text-[#3E75AC] my-2">
            <ErrorIcon
              sx={{ color: '#3E75AC', width: '20px', height: '20px' }}
            />
            {PAGE_TEXT.HELPER_TEXT.POINTS_TO_NOTE}
          </span>
          <h2>{PAGE_TEXT.HELPER_TEXT.NOTE_1}</h2>
          <h2>{PAGE_TEXT.HELPER_TEXT.NOTE_2}</h2>
        </div>
      )}

      {step === 1 ? (
        <Select
          className="-mt-8 mb-12 w-full"
          label={COMMON_PAGE_TEXT.SELECT}
          options={dependantVisaOptions}
          required={formikStatus.gender}
          id="signup-dependant-visa-options"
          name="identityType"
          value={formik.values.identityType}
          onChange={async e => {
            await formik.setFieldValue('identityType', e.target.value);
            setEntryDescription(
              e.target.value === IDENTITY_TYPE.DEPENDANT_VISA_UNDER_18
                ? PAGE_TEXT.IDENTITY_FLOW.DEPENDANT_VISA.BELOW_18.DESC
                : PAGE_TEXT.IDENTITY_FLOW.DEPENDANT_VISA.ABOVE_18.DESC,
            );
          }}
          onBlur={formik.handleBlur}
        />
      ) : null}
      {entryDescription ? (
        <div className="flex justify-center my-8">
          <div className="p-2 text-white font-bold bg-[#951D1D] rounded text-center max-w-md">
            {entryDescription}
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};
